
import React, { useState, useEffect, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css'; // Import global styles
import { db } from './services/db';
import { User, SiteAvailability } from './types';
import { GlobalBanner } from './components/GlobalBanner';
import { OfflineOverlay } from './components/OfflineOverlay';
import { ToastContainer, showToast } from './components/ui/Toast';
import { ReportCard } from './components/dashboard/ReportCard';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { KeepAlive } from './components/KeepAlive';
import { MaintenancePage } from './pages/MaintenancePage';
import { VerifyReportPage } from './pages/VerifyReportPage';
import { motion, AnimatePresence } from 'motion/react';

// Lazy Load Pages
const LandingPage = React.lazy(() => import('./pages/LandingPage').then(module => ({ default: module.LandingPage })));
const AuthPage = React.lazy(() => import('./pages/AuthPage').then(module => ({ default: module.AuthPage })));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage').then(module => ({ default: module.RegisterPage })));
const ResultCheckPage = React.lazy(() => import('./pages/ResultCheckPage').then(module => ({ default: module.ResultCheckPage })));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage').then(module => ({ default: module.DashboardPage })));
const TestimonialsPage = React.lazy(() => import('./pages/TestimonialsPage').then(module => ({ default: module.TestimonialsPage })));

db.init();

// Simple Loader for Suspense Fallback
const PageLoader = () => (
  <div className="h-screen w-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const ThemeInitializer = ({ user }: { user: User | null }) => {
  const { setTheme } = useTheme();
  
  useEffect(() => {
    if (user?.preferences?.theme) {
      setTheme(user.preferences.theme);
    } else {
      const storedTheme = localStorage.getItem('theme') as any;
      if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
        setTheme(storedTheme);
      }
    }
  }, [user, setTheme]);

  return null;
};

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center p-6 text-center font-['Inter',sans-serif]">
          <div className="bg-white p-8 rounded-3xl shadow-xl max-w-md w-full border border-slate-100">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h2>
            <p className="text-slate-500 text-sm mb-6 leading-relaxed">
              An unexpected error occurred while loading this page. Our team has been notified.
            </p>
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-left mb-6 font-mono text-xs text-slate-600 max-h-40 overflow-auto break-words font-sans">
              {this.state.error?.message || "Unknown error"}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const App = () => {
  const [view, setView] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    
    // Check for Supabase auth hash fragments (invite, recovery)
    if (window.location.hash && (window.location.hash.includes('type=invite') || window.location.hash.includes('type=recovery'))) {
      return 'dashboard'; // Let the dashboard handle the password reset prompt
    }

    if (path === '/admin1' || path === '/dashboard1') {
      return 'dashboard';
    }
    if (path === '/result' || path.startsWith('/result/')) {
      return 'verify';
    }
    return urlParams.get('view') || 'landing';
  });
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceConfig, setMaintenanceConfig] = useState<SiteAvailability | null>(null);
  const [userIp, setUserIp] = useState<string | null>(null);

  // Check for active session and maintenance on load
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

      const checkStatus = async () => {
        console.log('[App] Starting initialization...');
        const startTime = Date.now();
        try {
          // Parallelize independent checks
          const [userResult, configResult, ipResult] = await Promise.allSettled([
            db.getCurrentUser(),
            db.getSiteAvailability(),
            (async () => {
              try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 2000);
                const res = await fetch('https://api.ipify.org?format=json', { signal: controller.signal });
                clearTimeout(timeoutId);
                return await res.json();
              } catch (e) {
                return null;
              }
            })()
          ]);

          console.log(`[App] Core checks completed in ${Date.now() - startTime}ms`);

          if (userResult.status === 'fulfilled') {
            const u = userResult.value;
            setUser(u);
            
            if (u) {
              const urlParams = new URLSearchParams(window.location.search);
              const requestedView = urlParams.get('view');
              const path = window.location.pathname;
              
              if (requestedView === 'result_check') {
                setView('result_check');
              } else if (requestedView === 'verify' || path === '/result' || path.startsWith('/result/')) {
                setView('verify');
              } else if (requestedView === 'maintenance_preview') {
                setView('maintenance_preview');
              } else if (urlParams.get('view_report') === 'true') {
                // Stay on landing
              } else {
                setView('dashboard');
                if (window.location.search || (path !== '/' && path !== '/admin1' && path !== '/dashboard1')) {
                    window.history.replaceState({}, document.title, path === '/admin1' || path === '/dashboard1' ? path : '/');
                }
              }
            } else {
              const path = window.location.pathname;
              const urlParams = new URLSearchParams(window.location.search);
              const requestedView = urlParams.get('view');
              if (path === '/admin1' || path === '/dashboard1') {
                setView('dashboard');
              } else if (requestedView === 'verify' || path === '/result' || path.startsWith('/result/')) {
                setView('verify');
              }
            }
          }

          if (configResult.status === 'fulfilled') {
            const config = configResult.value;
            setMaintenanceConfig(config);
            unsubscribe = db.subscribeToAvailability((newConfig) => {
              setMaintenanceConfig(newConfig);
            });
          }

          if (ipResult.status === 'fulfilled' && ipResult.value) {
            setUserIp(ipResult.value.ip);
          }

        } catch (error) {
          console.error('[App] Error during app initialization:', error);
          setView('landing');
        } finally {
          setLoading(false);
          console.log(`[App] Initialization finished in ${Date.now() - startTime}ms`);
        }
      };
    checkStatus();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Auto logout non-admins during maintenance
  useEffect(() => {
    const isWhitelisted = userIp && maintenanceConfig?.whitelist?.includes(userIp);
    const isPlatformAdmin = user?.role === 'platform_admin';
    if (maintenanceConfig?.isMaintenanceMode && user && !isPlatformAdmin && !isWhitelisted) {
      db.logout().then(() => setUser(null));
    }
  }, [maintenanceConfig, user, userIp]);

  // Auto logout on 3 minutes of inactivity
  useEffect(() => {
    if (!user) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const logout = async () => {
      console.log('User inactive for 3 minutes. Logging out...');
      await db.logout();
      setUser(null);
      showToast('You have been logged out due to inactivity', 'info');
      setView('login');
    };

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(logout, 3 * 60 * 1000);
    };

    resetTimer();

    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    const handleActivity = () => resetTimer();
    
    events.forEach(e => window.addEventListener(e, handleActivity));

    return () => {
      clearTimeout(timeoutId);
      events.forEach(e => window.removeEventListener(e, handleActivity));
    };
  }, [user]);

  if (loading) {
    return <PageLoader />;
  }

  // Check for maintenance mode
  const isWhitelisted = userIp && maintenanceConfig?.whitelist?.includes(userIp);
  const isPlatformAdmin = user?.role === 'platform_admin';
  
  const path = window.location.pathname;
  const isAuthBypassRoute = path === '/admin1' || path === '/dashboard1';

  if (maintenanceConfig?.isMaintenanceMode) {
    if (isAuthBypassRoute) {
      // On the bypass route, only allow non-logged in users (to login) or platform admins
      if (user && !isPlatformAdmin && !isWhitelisted) {
        return <MaintenancePage config={maintenanceConfig} />;
      }
    } else if (!isWhitelisted) {
      // On any other route, show maintenance page to EVERYONE (even admins) unless whitelisted
      return <MaintenancePage config={maintenanceConfig} />;
    }
  }

  if (view === 'maintenance_preview' && maintenanceConfig) {
    return <MaintenancePage config={maintenanceConfig} />;
  }

  // Check URL params for report viewing
  const urlParams = new URLSearchParams(window.location.search);
  const isViewingReport = urlParams.get('view_report') === 'true';

  let content;
  if (isViewingReport) {
    content = <ReportCard />;
  } else {
    switch(view) {
      case 'dashboard': content = user ? <DashboardPage user={user} setUser={setUser} setView={setView} maintenanceConfig={maintenanceConfig} /> : <AuthPage setView={setView} setUser={setUser} />; break;
      case 'login': content = <AuthPage setView={setView} setUser={setUser} />; break;
      case 'register': content = <RegisterPage setView={setView} />; break;
      case 'result_check': content = <ResultCheckPage setView={setView} />; break;
      case 'verify': content = <VerifyReportPage />; break;
      case 'testimonials': content = <TestimonialsPage setView={setView} />; break;
      default: content = <LandingPage setView={setView} />;
    }
  }

  return (
    <ThemeProvider>
      <ThemeInitializer user={user} />
      <KeepAlive />
      <GlobalBanner />
      <ToastContainer />
      <OfflineOverlay />
      <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode="wait">
          <motion.div
            key={view + (isViewingReport ? '_report' : '')}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            {content}
          </motion.div>
        </AnimatePresence>
      </Suspense>
    </ThemeProvider>
  );
};

const container = document.getElementById('root');
if (container) {
  if (!(container as any).__reactRoot) {
    (container as any).__reactRoot = createRoot(container);
  }
  (container as any).__reactRoot.render(
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
